<script setup lang="ts">
import _ from 'lodash'
import { onMounted, onUnmounted, watch } from 'vue'
import router from '@/router'
import { storeToRefs } from 'pinia'
import { AppBackground } from '@haskoning/vue-component-library'
import ConfirmDeleteDialog from '@/components/app/ConfirmDeleteDialog.vue'
import { useRoute } from 'vue-router'
import { useDjangoAuthStore } from '@/stores/authDjango'
import { useAuthorizationStore } from '@/stores/authorization'
import { useProjectStore } from '@/stores/project'
import { useCalculationStore } from '@/stores/calculation'
import { useProjectUserStore } from '@/stores/projectUser'
import type { ProjectUserType } from '@/stores/type/projectUser.type'

const route = useRoute()

const authStore = useDjangoAuthStore()
const authorizationStore = useAuthorizationStore()
const projectUserStore = useProjectUserStore()

const projectStore = useProjectStore()
const calculationStore = useCalculationStore()

const { currentItem: currentProjectItem } = storeToRefs(projectStore)
const { currentItem: currentCalculationItem } = storeToRefs(calculationStore)
const { items: projectUsers } = storeToRefs(projectUserStore)
const { user: currentUser } = storeToRefs(authStore)
const { items: roles } = storeToRefs(authorizationStore)

const applyCurrentProjectRole = () => {
  if (!currentUser.value?.id) return
  if (!roles.value?.length) return

  const currentProjectUser = projectUsers.value.find(
    (item: ProjectUserType) => item.user === currentUser.value?.id
  )
  if (!currentProjectUser?.role) return

  authorizationStore.currentRoles = []
  authorizationStore.setCurrentRole([currentProjectUser.role])
}

onMounted(async () => {
  const projectId = String(route.params.id ?? '')
  const calculationId = String(route.params.calculationId ?? '')

  if (!currentUser.value) {
    await authStore.reAuthenticate()
  }

  await Promise.all([
    projectStore.fetchOptions(),
    projectStore.fetchItem(parseInt(projectId)),
    calculationStore.fetchOptions(),
    calculationStore.fetchItem(parseInt(calculationId))
  ])

  if (!currentProjectItem.value || _.isEmpty(currentProjectItem.value)) {
    await router.push({ name: 'not-found' })
  }

  if (!currentCalculationItem.value || _.isEmpty(currentCalculationItem.value)) {
    await router.push({ name: 'not-found' })
  }

  if (currentProjectItem.value) {
    const { id } = currentProjectItem.value

    if (id) {
      await projectUserStore.fetchItems({ search: id })
      await authorizationStore.fetchItems()
      applyCurrentProjectRole()
    }
  }
})

watch([currentUser, projectUsers, roles], () => {
  applyCurrentProjectRole()
})

onUnmounted(() => {
  projectStore.resetAll()
  calculationStore.resetAll()
})
</script>

<template>
  <div style="display: flex; flex-direction: column" class="fill-height">
    <AppBackground>
      <router-view></router-view>
    </AppBackground>
    <ConfirmDeleteDialog />
  </div>
</template>
